<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { getChargemapUrl } from '~/utils/url'
import useUserStore from '~/store/user'
import useNetworkStore from '~/store/network'
import DownloadApp from '~/components/Networks/DownloadApp.vue'
import OrderPass from '~/components/Networks/OrderPass.vue'
import DataComponent from '~/components/Networks/Data.vue'
import Ranking from '~/components/Networks/Ranking.vue'
import Faq from '~/components/Networks/Faq.vue'
import { ChargemapCtaIcon } from '#cmui/types'
import Map from '~/components/Map/map.vue'
import ServiceMessage from '~/components/Pools/ServiceMessage.vue'
import { ServiceMessageType } from '~/interfaces/UI'
import type { H3Event } from 'h3'

const config = useRuntimeConfig()
const route = useRoute()
const { t, locale, n } = useI18n()

const { hostname } = useRequestURL()
const subDomain = hostname?.split('.')[0]

const userStore = useUserStore()
const networkStore = useNetworkStore()

const locales = ref(['de', 'en', 'es', 'fr', 'it', 'nl', 'pt'])

const title = computed(() =>
    t('network.meta_title').replace(':network:', networkStore.name)
)

const description = computed(() =>
    t('network.description').replace(':network:', networkStore.name)
)

const faqForJsonLd = computed(() => [
    {
        '@type': 'Question',
        name: t('networks.faq.item1.question', {
            name: networkStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text: t('networks.faq.item1.answer1', {
                name: networkStore.name,
                answer2: t(
                    'networks.faq.item1.answer2',
                    networkStore.networkData?.totalStationsCount || 0
                ),
                answer3: t(
                    'networks.faq.item1.answer3',
                    networkStore.networkData?.totalEvsesCount || 0
                ),
                answer4: t('networks.faq.item1.answer4', {
                    chargemapMap: t('networks.faq.chargemap_map')
                })
            })
        }
    },
    {
        '@type': 'Question',
        name: t('networks.faq.item2.question', {
            name: networkStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text: t(
                `networks.faq.item2.answer_${networkStore.compatiblePrefix}`,
                {
                    name: networkStore.name,
                    chargemapPass: t('chargemap_pass')
                }
            )
        }
    },
    {
        '@type': 'Question',
        name: t('networks.faq.item3.question', {
            name: networkStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text:
                `1. ${t('networks.faq.item3.answer1')} ${t('networks.faq.item3.app')}.\n` +
                `2. ${t('networks.faq.item3.answer2', {
                    name: networkStore.name
                })}\n` +
                `3. ${t('networks.faq.item3.answer3')}`
        }
    },
    {
        '@type': 'Question',
        name: t('networks.faq.item5.question', {
            name: networkStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text:
                t('networks.faq.item5.answer1', {
                    name: networkStore.name
                }) +
                `\n\n${t('networks.faq.item5.answer2')}` +
                `\n\n${t('networks.faq.item5.answer3', {
                    name: networkStore.name
                })}` +
                `\n\n${t('networks.faq.item5.answer4', {
                    part1: t('networks.faq.item5.answer4_1'),
                    part3: t('networks.faq.network_map', {
                        name: networkStore.name
                    })
                })}`
        }
    },
    {
        '@type': 'Question',
        name: t('networks.faq.item6.question', {
            name: networkStore.name
        }),
        acceptedAnswer: {
            '@type': 'Answer',
            text: networkStore.isCompatible
                ? t(
                      `networks.faq.item6.answer_${networkStore.compatiblePrefix}`,
                      {
                          name: networkStore.name,
                          networkMap: t('networks.faq.network_map', {
                              name: networkStore.name
                          })
                      }
                  )
                : t(
                      `networks.faq.item6.answer_${networkStore.compatiblePrefix}1`,
                      {
                          name: networkStore.name
                      }
                  ) +
                  `\n\n${t(
                      `networks.faq.item6.answer_${networkStore.compatiblePrefix}2`,
                      {
                          name: networkStore.name
                      }
                  )}`
        }
    },
    {
        '@type': 'Question',
        name: t('networks.faq.item7.question', { name: networkStore.name }),
        acceptedAnswer: {
            '@type': 'Answer',
            text:
                t('networks.faq.item7.answer1', {
                    part2: t('networks.faq.item7.answer1_2')
                }) +
                '\n\n' +
                '1. ' +
                t(
                    `networks.faq.item7.answer2_${networkStore.compatiblePrefix}`,
                    {
                        pass: t('networks.faq.chargemap_pass_multi')
                    }
                ) +
                '\n' +
                '2. ' +
                t('networks.faq.item7.answer3', {
                    part2: '\n' + t('networks.faq.item7.answer3_2')
                }) +
                '\n' +
                '3. ' +
                t(
                    `networks.faq.item7.answer4_${networkStore.compatiblePrefix}`
                ) +
                (networkStore.isCompatible
                    ? '\n' + '4. ' + t('networks.faq.item7.answer5')
                    : '') +
                '\n\n' +
                t('networks.faq.item7.answer6', { name: networkStore.name }) +
                '\n\n' +
                t('word.order_your_pass')
        }
    }
])

const computedFaqObject: any = computed(() => {
    const array = [...faqForJsonLd.value]

    if (networkStore.isIonity) {
        array.push({
            '@type': 'Question',
            name: t('networks.faq.item4.question', {
                name: networkStore.name
            }),
            acceptedAnswer: {
                '@type': 'Answer',
                text: t('networks.faq.item4.answer', {
                    subscribe: t('networks.faq.subscribe')
                })
            }
        })
    }

    return array
})

const scrollToMap = () => {
    const mapElement = document.getElementById('networks-map-title')

    if (mapElement) {
        mapElement.scrollIntoView({ behavior: 'smooth' })
    }
}

useSeoMeta({
    title,
    ogTitle: title,
    description,
    ogDescription: description,
    robots: {
        noindex: config.public.NODE_ENV !== 'production' || subDomain === 'map'
    }
})

useAsyncData('network data', async () => {
    const event = useRequestEvent() as H3Event
    // Allows accents in the network slug
    const slugInRoute = getSafeName(route.params.networkSlug as string) || ''
    networkStore.slug = slugInRoute.toLowerCase()
    await networkStore.fetchNetworkData()
    await networkStore.fetchNetworkStatistics()
    await networkStore.fetchTopTenNetworks()
    await networkStore.fetchRankedNetworks()

    event.node.res.setHeader(
        'Cached-Data-Network-Detail-Date',
        networkStore.cachedDataNetworkDetailDate
    )
    event.node.res.setHeader(
        'Cached-Data-Network-Statistics-Date',
        networkStore.cachedDataNetworkStatisticsDate
    )

    event.node.res.setHeader(
        'Cached-Data-Network-Top-Ten-Networks-Date',
        networkStore.cachedDataNetworkTopTenNetworksDate
    )

    event.node.res.setHeader(
        'Cached-Data-Network-Ranked-Networks-Date',
        networkStore.cachedDataNetworkRankedNetworksDate
    )

    return true
})

const jsonLdGraphArray: any = computed(() => {
    const graphArray: any[] = [
        {
            '@type': 'FAQPage',
            mainEntity: computedFaqObject.value
        }
    ]

    if (networkStore.ratingsCount > 0) {
        graphArray.push({
            '@type': 'Organization',
            name: title.value,
            brand: networkStore.name,
            aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: n(networkStore.globalNote, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 1
                }),
                reviewCount: String(networkStore.ratingsCount)
            }
        })
    } else {
        graphArray.push({
            '@type': 'Organization',
            name: title.value,
            brand: networkStore.name,
            aggregateRating: {
                '@type': 'AggregateRating',
                reviewCount: '0'
            }
        })
    }

    return graphArray
})

useJsonld(() => ({
    '@context': 'https://schema.org',
    '@graph': jsonLdGraphArray.value
}))
</script>

<template>
    <Head>
        <Link
            v-for="l in locales"
            :key="l"
            rel="alternate"
            :hreflang="l"
            :href="getChargemapUrl(route.fullPath, l)"
        />
        <Link
            rel="alternate"
            hreflang="x-default"
            :href="getChargemapUrl(route.fullPath, 'en')"
        />
    </Head>
    <NuxtLayout name="seo">
        <i18n-t keypath="networks.title" tag="h1" scope="global">
            <template #network>
                <span>
                    {{ networkStore.name }}
                </span>
            </template>
        </i18n-t>
        <i18n-t keypath="networks.intro" tag="p" scope="global">
            <template #network>
                <span>{{ networkStore.name }}</span>
            </template>
            <template #zones>
                <span>
                    {{
                        $t(
                            'networks.charging_zones',
                            networkStore.chargingZonesNumber
                        )
                    }}
                </span>
            </template>
        </i18n-t>
        <div
            class="flex md:hidden flex-col items-center justify-center gap-6 pt-6"
        >
            <DownloadApp />
            <OrderPass
                v-if="!userStore.userAlreadyHasAPass"
                :compatible-prefix="networkStore.compatiblePrefix"
            />
        </div>

        <div
            v-if="!userStore.userId || !userStore.userAlreadyHasAPass"
            class="hidden md:grid justify-center items-center gap-7 w-full px-11 pt-8"
            :class="{
                'grid-cols-2':
                    !userStore.userId && !userStore.userAlreadyHasAPass,
                'grid-cols-1': userStore.userId || userStore.userAlreadyHasAPass
            }"
        >
            <ChargemapCta
                v-if="!userStore.userId"
                class="w-full h-full"
                :cta-text="t('join_chargemap.signup')"
                :image-path="`${config.app.cdnURL}/images/ChargemapPassMobile.svg`"
                :icon="ChargemapCtaIcon.IconUserCircle"
                :href="getChargemapUrl('/register', locale)"
                icon-is-solid
            >
                <template #text>
                    <i18n-t keypath="networks.cta_signup.text" scope="global">
                        <template #members>
                            <span>
                                {{ t('networks.cta_signup.members') }}
                            </span>
                        </template>
                    </i18n-t>
                </template>
            </ChargemapCta>
            <ChargemapCta
                v-if="!userStore.userId || !userStore.userAlreadyHasAPass"
                class="w-full h-full"
                :cta-text="t('word.order_your_pass')"
                :image-path="`${config.app.cdnURL}/images/PassTroncated.svg`"
                :icon="ChargemapCtaIcon.IconCaddie"
                :href="getChargemapUrl('/pass/purchase/step1', locale)"
                icon-is-solid
            >
                <template #text>
                    <i18n-t
                        scope="global"
                        :keypath="`networks.cta_charge.${networkStore.compatiblePrefix}.1`"
                    >
                        <template #network>
                            {{ networkStore.name }}
                        </template>
                    </i18n-t>
                    {{ ' ' }}
                    <i18n-t
                        scope="global"
                        :keypath="`networks.cta_charge.${networkStore.compatiblePrefix}.2`"
                    >
                        <template #thousandNetworks>
                            <span>
                                {{ t('networks.cta_charge.thousand_networks') }}
                            </span>
                        </template>
                    </i18n-t>
                </template>
            </ChargemapCta>
        </div>

        <i18n-t
            id="networks-map-title"
            keypath="networks.map_title"
            tag="h2"
            scope="global"
        >
            <template #network>
                <span>
                    {{ networkStore.name }}
                </span>
            </template>
        </i18n-t>

        <ServiceMessage
            v-if="networkStore.warningMessage"
            class="px-2 md:px-11"
            :text="networkStore.warningMessage"
            :type="ServiceMessageType.Negative"
        />

        <div class="overflow-hidden px-2 md:px-11">
            <Map
                class="!rounded-10 md:!rounded-20"
                :network-id="networkStore.id"
                :default-center="networkStore.defaultCenter"
                :default-zoom="networkStore.isTesla ? 3 : 6.5"
            />
        </div>

        <DataComponent
            v-if="
                networkStore.ratingsCount >= 10 ||
                networkStore.checkinsCount >= 10
            "
        />
        <Ranking />
        <Faq @scrollToMap="scrollToMap" />
    </NuxtLayout>
</template>

<style lang="scss" scoped>
h1 {
    @apply text-center py-6 md:pt-11 md:pb-2.5 px-4 lg:px-0 text-3xl md:text-5xl leading-none lg:leading-10;
}

h2 {
    @apply text-center pt-9 pb-5 md:py-12 text-2xl md:text-4xl font-bold leading-normal lg:leading-10;
}

span {
    @apply text-sky-500;
}

p {
    @apply text-center text-neutral-900 text-base md:text-xl font-medium font-inter leading-normal lg:leading-8 px-4 lg:px-0;
}
</style>
